import { ERROR_MESSAGE_CONTACT, REFRESH_TOKEN_URL } from '../shared/constants';
import { getEnvironment } from '../env/env';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * What fetchRequest resolves to (it never rejects): the parsed JSON body, `true` for a
 * 'No Content' response, or an Error carrying the server's (or a generic) message.
 */
export type FetchResult<T> = T | true | Error;

// The SSO refresh endpoint's body: a new access token, or an error.
export interface RefreshTokenResponse {
  access_token?: string;
  error?: { status?: number };
}

export const fetchRequest = async <T = Record<string, unknown>>(
  method: HttpMethod,
  url: string,
  data?: Record<string, unknown> | null,
  headers?: Record<string, string>,
  mode?: RequestMode,
): Promise<FetchResult<T>> => {
  const mergedHeaders: Record<string, string> = {
    Accept: 'application/json, application/xml, text/plain, text/html, *.*',
    'Content-Type': 'application/json; charset=utf-8',
    ...headers,
  };

  let isError = false;

  const params: RequestInit = {
    method,
    headers: mergedHeaders,
  };

  if (data) {
    params.body = JSON.stringify(data);
  }

  if (mode) {
    params.mode = mode;
  }

  return fetch(url, params)
    .then((response) => {
      isError = !response.ok;
      if (!response.ok) {
        return response.json();
      } else if (response.statusText === 'No Content') {
        return response.ok;
      } else {
        return response.json();
      }
    })
    .then((response) => {
      if (!isError) {
        return response;
      } else {
        let errorMessage = '';
        JSON.stringify(response, (_key: string, value: unknown) => {
          if (typeof value === 'string') {
            errorMessage += errorMessage ? `, ${value}` : value;
          }
          return value;
        });
        return new Error(errorMessage);
      }
    })
    .catch((error) => {
      console.error(error);
      return new Error(ERROR_MESSAGE_CONTACT);
    });
};

export const refreshToken = async (
  token: string | null,
): Promise<FetchResult<RefreshTokenResponse>> => {
  const environment = await getEnvironment();
  const url = `${environment.APP_SSO_BASE_URL}/${REFRESH_TOKEN_URL}`;

  return fetchRequest<RefreshTokenResponse>('POST', url, { refresh_token: token });
};
